// Homes, substations, plants, taps. Syncs display objects to World + DispatchResult each frame.
import { Container, Graphics, Polygon } from 'pixi.js';
import type { DispatchResult, Id, PlantType, World } from '../sim';
import { dist } from '../sim';
import { palette, plantColor } from './palette';
import { Tweens, easeOutBack, easeOutCubic } from './tween';

export type Pick = { kind: 'plant' | 'substation' | 'tap'; id: Id };

export interface EntityLayer {
  root: Container;
  /** night: 0 by day, 1 at deep night. Fed homes glow brighter as it rises. */
  sync(
    world: World,
    dispatch: DispatchResult,
    selected: Pick | null,
    dtMs: number,
    night: number,
  ): void;
  onPick: (pick: Pick) => void;
}

const HOME_R = 3.2;
const SUB_R = 9;
const PLANT_R = 12;
const HIT_R = 24; // map units; comfortably ≥ 44 css px at typical zoom

export function drawPlantGlyph(g: Graphics, type: PlantType, r = PLANT_R): void {
  g.clear();
  const color = plantColor(type);
  switch (type) {
    case 'thermal': {
      g.roundRect(-r, -r * 0.6, r * 2, r * 1.6, 3).fill({ color });
      g.rect(r * 0.15, -r * 1.25, r * 0.45, r * 0.7).fill({ color });
      break;
    }
    case 'solar': {
      g.rect(-r, -r * 0.7, r * 2, r * 1.4).fill({ color });
      g.moveTo(-r * 0.33, -r * 0.7).lineTo(-r * 0.33, r * 0.7);
      g.moveTo(r * 0.33, -r * 0.7).lineTo(r * 0.33, r * 0.7);
      g.moveTo(-r, 0).lineTo(r, 0);
      g.stroke({ color: palette.land, width: 1.2 });
      break;
    }
    case 'wind': {
      g.rect(-1.2, -r * 0.2, 2.4, r * 1.3).fill({ color });
      for (let i = 0; i < 3; i++) {
        const a = -Math.PI / 2 + (i * Math.PI * 2) / 3;
        g.moveTo(0, -r * 0.2).lineTo(Math.cos(a) * r, -r * 0.2 + Math.sin(a) * r);
      }
      g.stroke({ color, width: 2.4, cap: 'round' });
      break;
    }
    default: {
      g.circle(0, 0, r * 0.9).fill({ color });
    }
  }
}

export function createEntityLayer(tweens: Tweens): EntityLayer {
  const root = new Container();
  const radii = new Container();
  const lines = new Container();
  const homes = new Container();
  const nodes = new Container();
  root.addChild(radii, lines, homes, nodes);

  const homeViews = new Map<Id, { g: Graphics; status: string; seed: number; night: number }>();
  const subViews = new Map<
    Id,
    { g: Graphics; disc: Graphics; capacity: number; x: number; y: number }
  >();
  const plantViews = new Map<Id, { g: Graphics }>();
  const tapViews = new Map<Id, { g: Graphics; progress: { v: number } }>();

  const layer: EntityLayer = {
    root,
    onPick: () => undefined,
    sync(world, dispatch, selected, dtMs, night) {
      // Quantise so a slow twilight does not redraw every home every frame.
      nightLevel = Math.round(night * 12) / 12;
      syncHomes(world, dispatch, dtMs);
      syncSubstations(world, dispatch, selected);
      syncPlants(world, selected);
      syncTaps(world, dispatch, selected);
    },
  };

  let clock = 0;
  let nightLevel = 0;

  function syncHomes(world: World, dispatch: DispatchResult, dtMs: number): void {
    clock += dtMs;
    const seen = new Set<Id>();
    for (const h of world.homes) {
      seen.add(h.id);
      let v = homeViews.get(h.id);
      if (!v) {
        const g = new Graphics();
        g.position.set(h.x, h.y);
        homes.addChild(g);
        v = { g, status: '', seed: hash(h.id), night: -1 };
        homeViews.set(h.id, v);
        tweens.to(g.scale, 'x', 1, 450, easeOutBack, { from: 0 });
        tweens.to(g.scale, 'y', 1, 450, easeOutBack, { from: 0 });
      }
      const fedNow = dispatch.homeFed[h.id] === true;
      const status = h.status === 'dark' ? 'dark' : fedNow ? 'fed' : 'starved';
      if (status !== v.status || v.night !== nightLevel) {
        v.status = status;
        v.night = nightLevel;
        drawHome(v.g, status, nightLevel);
      }
      if (status === 'starved') {
        // Flicker: irregular, per-home phase, faster as the outage meter fills.
        const rate = 6 + h.outage * 10;
        const t = clock / 1000 + v.seed;
        v.g.alpha = 0.45 + 0.55 * Math.abs(Math.sin(t * rate) * Math.sin(t * 1.7 + v.seed));
      } else {
        v.g.alpha = 1;
      }
    }
    for (const [id, v] of homeViews) {
      if (!seen.has(id)) {
        v.g.destroy();
        homeViews.delete(id);
      }
    }
  }

  function syncSubstations(world: World, dispatch: DispatchResult, selected: Pick | null): void {
    const seen = new Set<Id>();
    for (const s of world.substations) {
      seen.add(s.id);
      let v = subViews.get(s.id);
      if (!v) {
        const disc = new Graphics();
        disc.circle(0, 0, s.radius).fill({ color: palette.radius, alpha: 0.045 });
        disc.circle(0, 0, s.radius).stroke({ color: palette.radius, width: 1, alpha: 0.18 });
        disc.position.set(s.x, s.y);
        radii.addChild(disc);
        const g = new Graphics();
        g.position.set(s.x, s.y);
        g.eventMode = 'static';
        g.cursor = 'pointer';
        g.hitArea = { contains: (x: number, y: number) => x * x + y * y <= HIT_R * HIT_R };
        g.on('pointertap', (e) => {
          e.stopPropagation();
          layer.onPick({ kind: 'substation', id: s.id });
        });
        nodes.addChild(g);
        v = { g, disc, capacity: -1, x: s.x, y: s.y };
        subViews.set(s.id, v);
        tweens.to(g.scale, 'x', 1, 450, easeOutBack, { from: 0 });
        tweens.to(g.scale, 'y', 1, 450, easeOutBack, { from: 0 });
        tweens.to(disc.scale, 'x', 1, 600, easeOutCubic, { from: 0 });
        tweens.to(disc.scale, 'y', 1, 600, easeOutCubic, { from: 0 });
      }
      if (v.x !== s.x || v.y !== s.y) {
        v.x = s.x;
        v.y = s.y;
        for (const obj of [v.g, v.disc]) {
          tweens.to(obj.position, 'x', s.x, 500, easeOutCubic);
          tweens.to(obj.position, 'y', s.y, 500, easeOutCubic);
        }
      }
      const flow = dispatch.substations[s.id];
      const live = (flow?.feed ?? 0) > 1e-9;
      const isSel = selected?.kind === 'substation' && selected.id === s.id;
      const pressure = flow && flow.need > 0 ? Math.min(1, flow.feed / flow.need) : 0;
      drawSubstation(v.g, live, isSel, pressure);
    }
    for (const [id, v] of subViews) {
      if (!seen.has(id)) {
        v.g.destroy();
        v.disc.destroy();
        subViews.delete(id);
      }
    }
  }

  function syncPlants(world: World, selected: Pick | null): void {
    const seen = new Set<Id>();
    for (const p of world.plants) {
      seen.add(p.id);
      let v = plantViews.get(p.id);
      if (!v) {
        const g = new Graphics();
        g.position.set(p.x, p.y);
        g.eventMode = 'static';
        g.cursor = 'pointer';
        g.hitArea = { contains: (x: number, y: number) => x * x + y * y <= HIT_R * HIT_R };
        g.on('pointertap', (e) => {
          e.stopPropagation();
          layer.onPick({ kind: 'plant', id: p.id });
        });
        nodes.addChild(g);
        v = { g };
        plantViews.set(p.id, v);
        tweens.to(g.scale, 'x', 1, 450, easeOutBack, { from: 0 });
        tweens.to(g.scale, 'y', 1, 450, easeOutBack, { from: 0 });
      }
      const isSel = selected?.kind === 'plant' && selected.id === p.id;
      const online = world.time.day >= p.onlineDay;
      drawPlantGlyph(v.g, p.type);
      if (!online) v.g.alpha = 0.4;
      else v.g.alpha = 1;
      if (isSel) v.g.circle(0, 0, PLANT_R + 6).stroke({ color: palette.select, width: 2 });
    }
    for (const [id, v] of plantViews) {
      if (!seen.has(id)) {
        v.g.destroy();
        plantViews.delete(id);
      }
    }
  }

  function syncTaps(world: World, dispatch: DispatchResult, selected: Pick | null): void {
    const seen = new Set<Id>();
    for (const t of world.taps) {
      seen.add(t.id);
      const plant = world.plants.find((p) => p.id === t.plantId);
      const sub = world.substations.find((s) => s.id === t.substationId);
      if (!plant || !sub) continue;
      let v = tapViews.get(t.id);
      if (!v) {
        const g = new Graphics();
        g.eventMode = 'static';
        g.cursor = 'pointer';
        g.on('pointertap', (e) => {
          e.stopPropagation();
          layer.onPick({ kind: 'tap', id: t.id });
        });
        lines.addChild(g);
        v = { g, progress: { v: 0 } };
        tapViews.set(t.id, v);
        tweens.to(v.progress, 'v', 1, 500, easeOutCubic, { from: 0 });
      }
      const flow = dispatch.taps[t.id];
      const maxFlow = Math.max(
        1e-9,
        ...world.plants.map((p) => dispatch.plantAvailable[p.id] ?? 0),
      );
      const strength = flow ? Math.min(1, flow.delivered / maxFlow) : 0;
      const isSel = selected?.kind === 'tap' && selected.id === t.id;
      drawTap(
        v.g,
        plant.x,
        plant.y,
        sub.x,
        sub.y,
        v.progress.v,
        flow?.loss ?? 0,
        strength,
        t.highVoltage,
        flow?.live ?? true,
        isSel,
      );
    }
    for (const [id, v] of tapViews) {
      if (!seen.has(id)) {
        v.g.destroy();
        tapViews.delete(id);
      }
    }
  }

  return layer;
}

function drawHome(g: Graphics, status: string, night: number): void {
  g.clear();
  if (status === 'fed') {
    // By day a warm dot with a faint halo; by night a lamp, and the town glows.
    g.circle(0, 0, HOME_R * (2.2 + 3.8 * night)).fill({
      color: palette.homeGlow,
      alpha: 0.12 + 0.16 * night,
    });
    g.circle(0, 0, HOME_R * (2.2 + 1.2 * night)).fill({
      color: palette.homeGlow,
      alpha: 0.28 + 0.3 * night,
    });
    g.circle(0, 0, HOME_R).fill({ color: night > 0.5 ? 0xffd97a : palette.homeFed });
  } else if (status === 'starved') {
    g.circle(0, 0, HOME_R).fill({ color: palette.homeStarved });
  } else {
    g.circle(0, 0, HOME_R * 0.85).fill({ color: palette.homeDark, alpha: 0.6 });
  }
}

function drawSubstation(g: Graphics, live: boolean, selected: boolean, pressure: number): void {
  g.clear();
  g.circle(0, 0, SUB_R).fill({ color: palette.land });
  g.circle(0, 0, SUB_R).stroke({ color: palette.substation, width: 2.2, alpha: live ? 1 : 0.35 });
  // Inner dot shows how much of its need is met.
  g.circle(0, 0, SUB_R * 0.45 * (0.4 + 0.6 * pressure)).fill({
    color: live ? palette.homeFed : palette.muted,
    alpha: live ? 1 : 0.5,
  });
  if (selected) g.circle(0, 0, SUB_R + 5).stroke({ color: palette.select, width: 2 });
}

function drawTap(
  g: Graphics,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  progress: number,
  loss: number,
  strength: number,
  hv: boolean,
  live: boolean,
  selected: boolean,
): void {
  g.clear();
  const ex = x1 + (x2 - x1) * progress;
  const ey = y1 + (y2 - y1) * progress;
  const width = 1.4 + 3.2 * strength;
  const alpha = live ? Math.max(0.22, 0.95 * (1 - loss)) : 0.5;
  const color = !live ? palette.fault : hv ? palette.lineHV : palette.line;
  if (selected) {
    g.moveTo(x1, y1)
      .lineTo(ex, ey)
      .stroke({ color: palette.select, width: width + 5, alpha: 0.25, cap: 'round' });
  }
  g.moveTo(x1, y1).lineTo(ex, ey).stroke({ color, width, alpha, cap: 'round' });
  if (hv) {
    g.moveTo(x1, y1)
      .lineTo(ex, ey)
      .stroke({ color: palette.land, width: Math.max(0.6, width * 0.3), alpha: 0.9, cap: 'round' });
  }
  // Hit area: a quad around the line, wide enough to tap.
  const len = Math.max(1e-6, dist(x1, y1, x2, y2));
  const nx = (-(y2 - y1) / len) * 9;
  const ny = ((x2 - x1) / len) * 9;
  g.hitArea = new Polygon([x1 + nx, y1 + ny, x2 + nx, y2 + ny, x2 - nx, y2 - ny, x1 - nx, y1 - ny]);
}

function hash(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return (h % 1000) / 1000;
}
